import { isSingleReactionEmoji } from "@kraahl/contract";

export const CHAT_QUICK_REACTION_EMOJIS = ["👍", "❤️", "😂", "😮", "😢", "🙏"] as const;

export type ChatQuickReactionEmoji = (typeof CHAT_QUICK_REACTION_EMOJIS)[number];

export type ChatReactionEmoji = string;

export type ReactionToggleIntent =
  | { kind: "remove"; emoji: string }
  | { kind: "upsert"; emoji: string };

export const REACTION_EMOJI_DB_MAX_CHAR_LENGTH = 32;

export function reactionEmojiDbCharLength(value: string): number {
  return Array.from(value.normalize("NFC")).length;
}

export function normalizeReactionEmojiForDb(value: string): string | null {
  const normalized = value.normalize("NFC").trim();
  if (!isSingleReactionEmoji(normalized)) return null;
  if (reactionEmojiDbCharLength(normalized) > REACTION_EMOJI_DB_MAX_CHAR_LENGTH) return null;
  return normalized;
}

export function isAllowedReactionEmoji(value: string): boolean {
  return normalizeReactionEmojiForDb(value) !== null;
}

export function resolveReactionToggle(
  currentEmoji: string | null | undefined,
  nextEmoji: string,
): ReactionToggleIntent {
  const normalizedNext = nextEmoji.normalize("NFC");
  const normalizedCurrent = currentEmoji?.normalize("NFC") ?? null;
  if (normalizedCurrent === normalizedNext) {
    return { kind: "remove", emoji: normalizedNext };
  }
  return { kind: "upsert", emoji: normalizedNext };
}

export type ReactionRow = {
  message_id: string;
  emoji: string;
  user_id: string;
};

export type MessageReactionAggregate = {
  counts: Record<string, number>;
  viewerEmoji: string | null;
};

export function aggregateMessageReactions(
  rows: ReactionRow[],
  viewerUserId: string,
): Map<string, MessageReactionAggregate> {
  const byMessage = new Map<string, MessageReactionAggregate>();

  for (const row of rows) {
    const entry = byMessage.get(row.message_id) ?? { counts: {}, viewerEmoji: null };
    entry.counts[row.emoji] = (entry.counts[row.emoji] ?? 0) + 1;
    if (row.user_id === viewerUserId) {
      entry.viewerEmoji = row.emoji;
    }
    byMessage.set(row.message_id, entry);
  }

  return byMessage;
}

export function aggregateSingleMessageReactions(
  rows: ReactionRow[],
  viewerUserId: string,
): MessageReactionAggregate {
  const counts: Record<string, number> = {};
  let viewerEmoji: string | null = null;

  for (const row of rows) {
    counts[row.emoji] = (counts[row.emoji] ?? 0) + 1;
    if (row.user_id === viewerUserId) {
      viewerEmoji = row.emoji;
    }
  }

  return { counts, viewerEmoji };
}

export function sanitizeViewerReactionEmoji(value: string | null | undefined): string | null {
  if (!value) return null;
  return normalizeReactionEmojiForDb(value);
}
